import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { useMe } from '@/features/auth';
import type { AuthState } from '@/features/auth';
import { authCookies } from '@/api/cookies';
import { PageLoader } from '@/shared/ui/Loader';

// Guards читают состояние из нового RTK auth-slice (а не из legacy AuthContext).
// useMe дополнительно вызывается чтобы триггернуть фоновый fetch /me — на
// success он сам выставит status в 'authenticated' (см. useMe в features/auth).
//
// Legacy AuthContext (контекст-провайдер) остаётся вкручен в main.tsx до
// финальной зачистки старого стека, но guards перестают его читать.

const selectAuth = (state: { auth: AuthState }) => state.auth;

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAppSelector(selectAuth);
  const { isLoading } = useMe();
  const location = useLocation();

  if (status === 'pending-2fa') {
    return <Navigate to="/auth/2fa" replace state={{ from: location }} />;
  }
  // Пока статус не определён, а cookies на месте — ждём резолва /me, НЕ
  // редиректим на /login. Иначе при cold-load (deep-link / refresh / Ctrl+ЛКМ)
  // ловим переходный кадр: react-query уже отдал данные (isLoading=false), но
  // setAuthenticated диспатчится в useEffect (после рендера) — между ними есть
  // рендер со status==='unknown' && !isLoading. Старое условие
  // `status !== 'authenticated'` принимало его за гостя и кидало на /login →
  // дальше RedirectIfAuthed (статус уже стал authenticated) гнал на '/' →
  // index-route → /stat. Из-за этого открытие ЛЮБОГО роута в новой вкладке
  // сбрасывало на статистику.
  //
  // hasFullAuth() стабильно true весь cold-load (в отличие от isLoading),
  // поэтому переходного окна не остаётся. Если /me вернёт 401 —
  // detectUnauthorized чистит cookies + setGuest → status='guest' →
  // проваливаемся в редирект на /login ниже. Гость без cookies сюда не
  // попадает (hasFullAuth=false) и сразу уходит на /login.
  if (status === 'unknown' && (isLoading || authCookies.hasFullAuth())) {
    // Cold-load гейт рендерится ВМЕСТО всего лейаута (шелла ещё нет), поэтому
    // это загрузчик на весь экран, а не лоадер блока: у темы может быть своя
    // разметка (telecom — знак в кольце на красной волне).
    return <PageLoader />;
  }
  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status } = useAppSelector(selectAuth);
  // useMe тоже вызываем чтобы guest-флоу при наличии cookies не висел в
  // 'unknown' — данные подтянутся → status станет authenticated → редирект.
  useMe();

  if (status === 'authenticated') return <Navigate to="/" replace />;
  if (status === 'pending-2fa') return <Navigate to="/auth/2fa" replace />;
  return <>{children}</>;
}
